<template>
  <div class="icon-picker relative" :class="containerClass">
    <label v-if="labelText" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {{ labelText }}
    </label>

    <div class="flex gap-2">
      <div class="relative flex-1">
        <input
          ref="inputEl"
          :value="searchQuery"
          @input="onInput($event)"
          @focus="onFocus"
          type="text"
          :placeholder="placeholderText"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />

        <!-- Dropdown panel -->
        <div
          v-if="open"
          class="absolute z-20 mt-1 w-[min(28rem,90vw)] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden">
          <!-- Category chips -->
          <div
            class="flex flex-wrap gap-1 px-2 py-2 border-b border-gray-200 dark:border-gray-700 max-h-24 overflow-y-auto">
            <button
              type="button"
              @click.prevent="selectCategory(null)"
              :class="[
                'px-2 py-0.5 text-xs rounded-full border transition-colors',
                activeCategory === null
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600',
              ]">
              {{ tt('All', 'Vse') }}
            </button>
            <button
              v-for="cat in categories"
              :key="cat"
              type="button"
              @click.prevent="selectCategory(cat)"
              :class="[
                'px-2 py-0.5 text-xs rounded-full border transition-colors',
                activeCategory === cat
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600',
              ]">
              {{ cat }}
            </button>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
            {{ tt('Loading icons…', 'Nalaganje ikon…') }}
          </div>

          <!-- Grid -->
          <div v-else ref="gridEl" @scroll="onScroll" class="grid grid-cols-8 gap-1 p-2 max-h-72 overflow-y-auto">
            <button
              v-for="icon in visibleIcons"
              :key="icon.n"
              type="button"
              :title="icon.n"
              @click.prevent="selectIcon(icon.n)"
              :class="[
                'aspect-square flex items-center justify-center rounded transition-colors',
                modelValue === icon.n
                  ? 'bg-blue-100 dark:bg-blue-900/40 ring-1 ring-blue-500'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700',
              ]">
              <span class="material-symbols-outlined text-xl text-gray-700 dark:text-gray-300">{{ icon.n }}</span>
            </button>

            <div
              v-if="results.length === 0"
              class="col-span-8 p-3 text-center text-xs text-gray-500 dark:text-gray-400">
              {{ tt('No icons found', 'Ni najdenih ikon') }}
            </div>
            <div
              v-else-if="visibleIcons.length < results.length"
              class="col-span-8 p-2 text-center text-xs text-gray-400 dark:text-gray-500">
              {{ tt('Scroll for more…', 'Pomakni se za več…') }} ({{ visibleIcons.length }} / {{ results.length }})
            </div>
          </div>

          <div
            class="px-3 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 flex justify-between">
            <span>{{ matchLabel }}</span>
            <span class="font-mono">{{ modelValue || '—' }}</span>
          </div>
        </div>
      </div>

      <!-- Selected preview -->
      <div class="flex items-center justify-center w-12 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0">
        <span class="material-symbols-outlined text-2xl text-gray-700 dark:text-gray-300">{{
          modelValue || fallback
        }}</span>
      </div>
    </div>

    <p v-if="hint" class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ hint }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import {
  loadMaterialIcons,
  searchIcons,
  getCategories,
  sanitizeIconName,
  type IconMeta,
} from '../../composables/useMaterialIcons';
import { tt, consoleLocale } from '../../utils/consoleText';

interface Props {
  modelValue: string;
  label?: string;
  placeholder?: string;
  hint?: string;
  fallback?: string;
  containerClass?: string;
}

// The picker's own two strings are a pair, not a prop default: a call site that
// passes neither must still read in the reader's language. `hint` keeps its empty
// default (it is the call site's own sentence, passed translated).
const props = withDefaults(defineProps<Props>(), {
  hint: '',
  fallback: 'home',
  containerClass: '',
});

const labelText = computed(() => props.label ?? tt('Icon', 'Ikona'));
const placeholderText = computed(() => props.placeholder ?? tt('Search icons…', 'Išči ikone…'));

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const open = ref(false);
const loading = ref(false);
const searchQuery = ref(props.modelValue || '');
const activeCategory = ref<string | null>(null);
const results = ref<IconMeta[]>([]);
const visibleCount = ref(96);
const inputEl = ref<HTMLInputElement | null>(null);
const gridEl = ref<HTMLElement | null>(null);

const categories = ref<string[]>([]);

const visibleIcons = computed(() => results.value.slice(0, visibleCount.value));

/**
 * Slovene agrees a count with its number (`1 zadetek`, `2 zadetka`, `5 zadetkov`)
 * where English only has two forms. The same helper the status drawer and the
 * user drawer carry.
 */
function slCount(n: number, one: string, two: string, few: string, many: string): string {
  const lastTwo = n % 100;
  if (lastTwo === 1) return one;
  if (lastTwo === 2) return two;
  if (lastTwo === 3 || lastTwo === 4) return few;
  return many;
}

/** The panel's own `N match(es)` footer line, worded for the reader's language. */
const matchLabel = computed(() => {
  const n = results.value.length;
  if (consoleLocale() === 'en') return `${n} match${n === 1 ? '' : 'es'}`;
  return `${n} ${slCount(n, 'zadetek', 'zadetka', 'zadetki', 'zadetkov')}`;
});

async function ensureLoaded() {
  if (loading.value) return;
  loading.value = true;
  try {
    await loadMaterialIcons();
    categories.value = getCategories();
    runSearch();
  } finally {
    loading.value = false;
  }
}

function runSearch() {
  results.value = searchIcons({
    query: searchQuery.value,
    category: activeCategory.value,
    limit: 1000,
  });
  visibleCount.value = 96;
  if (gridEl.value) gridEl.value.scrollTop = 0;
}

function onInput(e: Event) {
  searchQuery.value = (e.target as HTMLInputElement).value;
  runSearch();
}

function onFocus() {
  open.value = true;
  ensureLoaded();
}

function selectCategory(cat: string | null) {
  activeCategory.value = cat;
  runSearch();
}

function selectIcon(name: string) {
  emit('update:modelValue', name);
  searchQuery.value = name;
  open.value = false;
}

function onScroll(e: Event) {
  const el = e.target as HTMLElement;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
    if (visibleCount.value < results.value.length) {
      visibleCount.value = Math.min(visibleCount.value + 96, results.value.length);
    }
  }
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.icon-picker')) {
    open.value = false;
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (val && val !== searchQuery.value && document.activeElement !== inputEl.value) {
      searchQuery.value = sanitizeIconName(val, props.fallback);
    }
  },
);

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
